import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import {
  loadRaidControlRealtimeSnapshot,
  loadRaidControlRealtimeHistory,
  markRaidControlRealtimeRead,
  resyncRaidControlRealtime,
  sendRaidControlRealtimeMessage,
  subscribeRaidControlRealtime,
  updateRaidControlRealtimeHeader,
} from './raidControlRealtimeClient.js';
import {
  raidControlCustomOrderArgs,
  raidControlOrderArgs,
} from '../domain/raidControlRpcPayload.js';
import {
  extractRaidControlTetrisShare,
  isRaidControlTetrisShareRef,
} from '../domain/raidControlTetrisShareCodec.js';

const ERROR_MESSAGES = {
  invalid_notice: '공지는 1,000자 이내로 작성해주세요.',
  message_not_found: '삭제되었거나 다른 시즌의 메시지는 고정할 수 없습니다.',
  chat_header_unsupported: '공지·고정 기능이 아직 준비되지 않았습니다.',
  chat_header_timeout: '공지·고정 저장이 지연되고 있습니다. 다시 시도해주세요.',
  chat_header_unavailable: '연결 상태를 확인한 뒤 다시 시도해주세요.',
  authentication_required: '로그인 세션을 다시 확인해주세요.',
  active_union_membership_required: '활성 유니온 가입 정보가 필요합니다.',
  union_manager_required: '공식 공격 지시는 주딱·파딱만 작성할 수 있습니다.',
  order_target_required: '공격 대상 멤버 또는 운영진만 상태를 변경할 수 있습니다.',
  target_member_not_found: '지시할 멤버를 찾지 못했습니다.',
  practice_record_not_found: '선택한 모의전 슬롯을 다시 확인해주세요.',
  boss_attack_confirmation_required: '다른 멤버가 같은 보스를 공격 중입니다. 확인 후 시작해주세요.',
  boss_already_in_progress: '같은 보스를 공격 중인 멤버가 있습니다. 완료 또는 보류 후 다시 시작해주세요.',
  member_order_already_in_progress: '이 멤버는 이미 다른 공격을 진행 중입니다. 완료 또는 보류 후 다시 시작해주세요.',
  order_transition_not_allowed: '현재 공격 상태에서는 해당 단계로 변경할 수 없습니다.',
  invalid_chat_message: '메시지는 1~500자로 입력해주세요.',
  invalid_practice_damage: '모의전 피해량은 0보다 큰 억 단위 숫자로 입력해주세요.',
  invalid_custom_order: '직접 작성 덱은 이름, 예상 피해량과 니케 5명을 모두 입력해주세요.',
  invalid_mention_target: '멘션할 멤버를 다시 확인해주세요.',
  invalid_nikke_refs: '공유할 니케 정보를 다시 확인해주세요.',
  reply_message_not_found: '답장할 메시지를 찾지 못했습니다.',
  raid_season_not_found: '선택한 레이드 차수를 다시 확인해주세요.',
  realtime_ticket_timeout: '실시간 채팅 연결 시간이 초과되었습니다. 다시 시도해주세요.',
  realtime_message_timeout: '메시지 전송 응답 시간이 초과되었습니다. 다시 시도해주세요.',
  message_save_failed: '메시지를 저장하지 못했습니다. 다시 시도해주세요.',
  realtime_read_timeout: '읽음 상태 저장 시간이 초과되었습니다. 다시 시도해주세요.',
};

const RAID_CONTROL_TIMELINE_CACHE_MS = 5_000;
const REALTIME_CHANNEL_RELEASE_DELAY_MS = 1_200;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const raidControlContextRequests = new Map();
const raidControlTimelineRequests = new Map();
const raidControlTimelineCache = new Map();
// 지시 목록과 실전 기록은 서로 다른 일로 바뀐다. 한 덩어리로 묶어 두면 공격 상태 하나를
// 바꿔도 둘 다 다시 받는다 - 화면을 열어 둔 사람 수만큼 조회가 두 배로 붙는다.
const raidControlOrdersCache = new Map();
const raidControlRecordsCache = new Map();
const raidControlOrdersRequests = new Map();
const raidControlRecordsRequests = new Map();
let timelineGeneration = 0;
const partGenerations = { orders: 0, records: 0 };
const raidControlSubscriptionStores = new Map();
const seasonRuntimeById = new Map();
const activeSeasonIdByUnion = new Map();
const bossNamesBySeason = new Map();
const orderMessageIds = new Map();
const realtimeMessageSeasonNos = new Map();

function client() {
  if (!hasSupabaseConfig || !supabase) throw new Error('서비스 연결 설정을 확인할 수 없습니다.');
  return supabase;
}

function controlError(error, fallback) {
  const raw = String(error?.message || '');
  const code = Object.keys(ERROR_MESSAGES).find((key) => raw.includes(key));
  const next = new Error(ERROR_MESSAGES[code] || raw || fallback);
  next.code = code || error?.code || 'unknown_error';
  return next;
}

function dedupeRaidControlRequest(store, key, load) {
  const existing = store.get(key);
  if (existing) return existing;
  const request = Promise.resolve().then(load);
  store.set(key, request);
  request.finally(() => {
    if (store.get(key) === request) store.delete(key);
  }).catch(() => {});
  return request;
}

// orders 와 records 는 '그 목록이 정말 바뀌었을 때'만 비운다. 채팅 한 줄, 공격 상태 하나에
// 둘 다 비우면, 다음 조회가 바뀌지도 않은 목록까지 160줄씩 다시 받아 온다.
function clearRaidControlTimelineCache({ orders = true, records = true } = {}) {
  timelineGeneration += 1;
  raidControlTimelineCache.clear();
  // A read started before an invalidation must not satisfy its reconciliation.
  raidControlTimelineRequests.clear();
  if (orders) {
    partGenerations.orders += 1;
    raidControlOrdersCache.clear();
    raidControlOrdersRequests.clear();
  }
  if (records) {
    partGenerations.records += 1;
    raidControlRecordsCache.clear();
    raidControlRecordsRequests.clear();
  }
}

// 세대 번호는 목록마다 따로 센다. 하나로 묶으면 채팅 한 줄이 올라올 때마다 번호가 밀려서,
// 말이 오가는 방에서는 두 목록 모두 캐시에 영영 들어가지 못한다.
function cachedPart(part, cache, requests, key, load) {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.cachedAt < RAID_CONTROL_TIMELINE_CACHE_MS) return Promise.resolve(cached.value);
  const generation = partGenerations[part];
  return dedupeRaidControlRequest(requests, key, async () => {
    const value = await load();
    if (generation === partGenerations[part]) cache.set(key, { cachedAt: Date.now(), value });
    return value;
  });
}

function normalizeContext(raw = {}) {
  const season = raw?.season && typeof raw.season === 'object' ? raw.season : null;
  return {
    unionId: String(raw.union_id || ''),
    viewerRole: String(raw.viewer_role || 'member'),
    season: season ? { id: String(season.id || ''), seasonNo: Number(season.season_no || 0), status: String(season.status || '') } : null,
    members: (Array.isArray(raw.members) ? raw.members : []).map((row) => ({ memberOpenid: String(row.member_openid || ''), nickname: String(row.nickname || '멤버'), syncLevel: Number(row.sync_level || 0) || null, userId: String(row.user_id || ''), avatarUrl: String(row.avatar_url || ''), role: String(row.role || 'member') })),
    practiceRecords: (Array.isArray(raw.practice_records) ? raw.practice_records : []).map((row) => ({ id: String(row.id || ''), memberOpenid: String(row.member_openid || ''), memberNickname: String(row.member_nickname || ''), bossNo: Number(row.boss_no || 0), slotNo: Number(row.slot_no || 0), title: String(row.title || ''), damageEok: Number(row.damage_eok || 0), deck: Array.isArray(row.deck) ? row.deck : [], used: Boolean(row.used), note: String(row.note || '') })),
  };
}

function registerContext(context) {
  if (!context?.unionId || !context?.season?.id || !context.season.seasonNo) return;
  seasonRuntimeById.set(context.season.id, {
    unionId: context.unionId,
    seasonId: context.season.id,
    seasonNo: Number(context.season.seasonNo),
    members: context.members || [],
  });
  if (context.season.status === 'active') activeSeasonIdByUnion.set(context.unionId, context.season.id);
}

async function resolveSeasonRuntime({ unionId = '', seasonId = '' } = {}) {
  const normalizedSeasonId = String(seasonId || '').trim();
  const normalizedUnionId = String(unionId || '').trim();
  if (normalizedSeasonId && seasonRuntimeById.has(normalizedSeasonId)) return seasonRuntimeById.get(normalizedSeasonId);

  let query = client().from('raid_seasons').select('id,union_id,season_no,status');
  if (normalizedSeasonId) query = query.eq('id', normalizedSeasonId);
  else if (normalizedUnionId) query = query.eq('union_id', normalizedUnionId).eq('status', 'active').order('season_no', { ascending: false }).limit(1);
  else throw new Error('raid_control_season_required');
  const { data, error } = normalizedSeasonId ? await query.maybeSingle() : await query.maybeSingle();
  if (error || !data?.id || !data?.season_no) throw controlError(error || new Error('raid_season_not_found'), '선택한 레이드 차수를 다시 확인해주세요.');
  const runtime = {
    unionId: String(data.union_id || normalizedUnionId),
    seasonId: String(data.id),
    seasonNo: Number(data.season_no),
    members: [],
  };
  seasonRuntimeById.set(runtime.seasonId, runtime);
  if (String(data.status || '') === 'active') activeSeasonIdByUnion.set(runtime.unionId, runtime.seasonId);
  return runtime;
}

function normalizeNikkeRef(raw = {}) {
  const source = raw && typeof raw === 'object' && !Array.isArray(raw)
    ? raw
    : { nikke_id: raw };
  return {
    nikkeId: String(source.nikke_id ?? source.nikkeId ?? source.code ?? source.id ?? ''),
    name: String(source.name ?? source.official_name ?? source.officialName ?? ''),
    imageUrl: String(source.image_url ?? source.imageUrl ?? ''),
  };
}

function normalizeOrder(row = {}) {
  return {
    id: String(row.id || ''),
    unionId: String(row.union_id ?? row.unionId ?? ''),
    seasonId: String(row.season_id ?? row.seasonId ?? ''),
    targetMemberOpenid: String(row.target_member_openid ?? row.targetMemberOpenid ?? ''),
    targetUserId: String(row.target_user_id ?? row.targetUserId ?? ''),
    targetNickname: String(row.target_nickname ?? row.targetNickname ?? '멤버'),
    bossNo: Number(row.boss_no ?? row.bossNo ?? 0),
    phaseNo: Number(row.phase_no ?? row.phaseNo ?? 0),
    practiceRecordId: String(row.practice_record_id ?? row.practiceRecordId ?? ''),
    practiceTitle: String(row.practice_title ?? row.practiceTitle ?? ''),
    expectedDamageEok: (row.expected_damage_eok ?? row.expectedDamageEok) == null ? null : Number(row.expected_damage_eok ?? row.expectedDamageEok),
    deck: Array.isArray(row.deck) ? row.deck.slice(0, 5) : [],
    note: String(row.note || ''),
    status: String(row.status || 'sent'),
    createdBy: String(row.created_by ?? row.createdBy ?? ''),
    acknowledgedAt: String(row.acknowledged_at ?? row.acknowledgedAt ?? ''),
    startedAt: String(row.started_at ?? row.startedAt ?? ''),
    completedAt: String(row.completed_at ?? row.completedAt ?? ''),
    createdAt: String(row.created_at ?? row.createdAt ?? ''),
    updatedAt: String(row.updated_at ?? row.updatedAt ?? ''),
  };
}

function normalizeMessage(row = {}, fallback = {}) {
  const event = row.event_data && typeof row.event_data === 'object' ? row.event_data : row.eventData && typeof row.eventData === 'object' ? row.eventData : {};
  const rawNikkeRefs = Array.isArray(row.nikke_refs) ? row.nikke_refs : Array.isArray(row.nikkeRefs) ? row.nikkeRefs : [];
  const tetrisShare = extractRaidControlTetrisShare(rawNikkeRefs);
  const sourceRecordKey = String(row.source_record_key ?? row.sourceRecordKey ?? event.record_key ?? '');
  return {
    id: String(row.id || ''),
    unionId: String(row.union_id ?? row.unionId ?? fallback.unionId ?? ''),
    seasonId: String(row.season_id ?? row.seasonId ?? fallback.seasonId ?? ''),
    authorUserId: String(row.author_user_id ?? row.authorUserId ?? ''),
    authorNickname: String(row.author_nickname ?? row.authorNickname ?? '멤버'),
    type: tetrisShare ? 'tetris_share' : String(row.message_type ?? row.type ?? 'text'),
    body: String(row.body || ''),
    stickerId: String(event.sticker_id || ''),
    stickerIds: Array.isArray(event.sticker_ids) ? event.sticker_ids.filter(id => typeof id === 'string').slice(0, 2) : [],
    orderId: String(row.order_id ?? row.orderId ?? ''),
    replyToId: String(row.reply_to_id ?? row.replyToId ?? ''),
    mentionedUserIds: (Array.isArray(row.mentioned_user_ids) ? row.mentioned_user_ids : Array.isArray(row.mentionedUserIds) ? row.mentionedUserIds : []).map(String),
    nikkeRefs: rawNikkeRefs.filter((ref) => !isRaidControlTetrisShareRef(ref)).map(normalizeNikkeRef).slice(0, 5),
    tetrisShare,
    sourceRecordKey,
    eventData: {
      recordKey: String(event.record_key || sourceRecordKey),
      memberOpenid: String(event.member_openid ?? event.memberOpenid ?? ''),
      memberNickname: String(event.member_nickname ?? event.memberNickname ?? ''),
      bossNo: Number(event.boss_no ?? event.bossNo ?? 0),
      bossName: String(event.boss_name ?? event.bossName ?? ''),
      phaseNo: Number(event.phase_no ?? event.phaseNo ?? 0),
      attackNo: Number(event.attack_no ?? event.attackNo ?? 0) || null,
      damageEok: Number(event.damage_eok ?? event.damageEok ?? 0),
      isKill: Boolean(event.is_kill ?? event.isKill),
      attackedAt: String(event.attacked_at ?? event.attackedAt ?? ''),
      sourceOrderKnown: Boolean(event.source_order_known ?? event.sourceOrderKnown),
      identityMethod: String(event.identity_method ?? event.identityMethod ?? ''),
      deck: Array.isArray(event.deck) ? event.deck.slice(0, 5) : [],
    },
    createdAt: String(row.created_at ?? row.createdAt ?? ''),
  };
}

function rememberRealtimeMessage(messageId, seasonNo) {
  const id = String(messageId || '');
  if (!id) return;
  realtimeMessageSeasonNos.set(id, Number(seasonNo || 0));
  while (realtimeMessageSeasonNos.size > 512) realtimeMessageSeasonNos.delete(realtimeMessageSeasonNos.keys().next().value);
}

function syntheticOrderMessage(order, runtime, bossNames = new Map()) {
  if (!order?.id) return null;
  const author = (runtime?.members || []).find((member) => member.userId === order.createdBy) || null;
  const bossName = bossNames.get(Number(order.bossNo)) || '보스';
  return {
    id: orderMessageIds.get(order.id) || `order:${order.id}`,
    unionId: order.unionId || runtime?.unionId || '',
    seasonId: order.seasonId || runtime?.seasonId || '',
    authorUserId: order.createdBy,
    authorNickname: author?.nickname || '운영진',
    type: 'attack_order',
    body: `${order.targetNickname || '멤버'} · ${bossName} ${order.phaseNo || '-'}단 공격 지시`,
    orderId: order.id,
    replyToId: '',
    mentionedUserIds: order.targetUserId ? [order.targetUserId] : [],
    nikkeRefs: [],
    tetrisShare: null,
    sourceRecordKey: '',
    eventData: {},
    createdAt: order.createdAt,
  };
}

function raidResultMessageId(sourceRecordKey) {
  const key = String(sourceRecordKey || '').trim();
  return key ? `result:${key}` : '';
}

export function syntheticRaidResultMessage(row, runtime, bossNames = new Map()) {
  const sourceRecordKey = String(row?.record_key ?? row?.recordKey ?? '').trim();
  if (!sourceRecordKey) return null;
  const bossNo = Number(row?.boss_no ?? row?.bossNo ?? 0);
  const phaseNo = Number(row?.phase_no ?? row?.phaseNo ?? 0);
  const attackNo = Number(row?.attack_no ?? row?.attackNo ?? 0) || null;
  const damageEok = Number(row?.damage_eok ?? row?.damageEok ?? 0);
  const memberOpenid = String(row?.member_openid ?? row?.memberOpenid ?? '');
  const memberNickname = String(row?.member_nickname ?? row?.memberNickname ?? '멤버');
  const attackedAt = String(row?.attacked_at ?? row?.attackedAt ?? '');
  const isKill = Boolean(row?.is_kill ?? row?.isKill);
  const bossName = bossNames.get(bossNo) || '보스';
  const damageText = Number.isFinite(damageEok) ? String(damageEok) : '0';
  const body = `${memberNickname} · ${bossName} ${phaseNo || '-'}단 실전 ${damageText}억${isKill ? ' · 처치' : ''}`;
  return {
    id: raidResultMessageId(sourceRecordKey),
    canonicalRecordId: String(row?.id || ''),
    unionId: String(row?.union_id ?? row?.unionId ?? runtime?.unionId ?? ''),
    seasonId: String(row?.season_id ?? row?.seasonId ?? runtime?.seasonId ?? ''),
    authorUserId: '',
    authorNickname: memberNickname,
    type: 'raid_result',
    body,
    orderId: '',
    replyToId: '',
    mentionedUserIds: [],
    nikkeRefs: [],
    tetrisShare: null,
    sourceRecordKey,
    eventData: {
      recordKey: sourceRecordKey,
      memberOpenid,
      memberNickname,
      bossNo,
      bossName,
      phaseNo,
      attackNo,
      damageEok,
      isKill,
      attackedAt,
      sourceOrderKnown: Boolean(attackedAt),
      identityMethod: attackedAt ? '' : 'unordered_content_multiset_v1',
      deck: Array.isArray(row?.deck) ? row.deck.slice(0, 5) : [],
    },
    createdAt: attackedAt || String(row?.created_at ?? row?.createdAt ?? row?.updated_at ?? row?.updatedAt ?? ''),
  };
}

async function loadBossNames(unionId, seasonId) {
  const key = `${unionId}:${seasonId}`;
  if (bossNamesBySeason.has(key)) return bossNamesBySeason.get(key);
  const { data, error } = await client().from('raid_bosses').select('boss_no,boss_name').eq('union_id', unionId).eq('season_id', seasonId);
  if (error) throw controlError(error, '보스 정보를 불러오지 못했습니다.');
  const names = new Map((data || []).map((row) => [Number(row.boss_no), String(row.boss_name || '보스')]));
  bossNamesBySeason.set(key, names);
  return names;
}

export function loadRaidControlContext(seasonNo = null) {
  const requestedSeasonNo = seasonNo == null ? null : Number(seasonNo);
  const requestKey = requestedSeasonNo == null ? 'current' : String(requestedSeasonNo);
  return dedupeRaidControlRequest(raidControlContextRequests, requestKey, async () => {
    const { data, error } = await client().rpc('get_raid_control_context', { requested_season_no: requestedSeasonNo });
    if (error) throw controlError(error, '레이드 지휘실 정보를 불러오지 못했습니다.');
    const context = normalizeContext(data || {});
    registerContext(context);
    return context;
  });
}

export function loadRaidControlTimeline({ unionId, seasonId, userId = '', limit = 160, ordersOnly = false, resultsOnly = false, skipRecords = false }) {
  const generation = timelineGeneration;
  const requestKey = [String(unionId || ''), String(seasonId || ''), String(userId || ''), Number(limit || 160), ordersOnly ? 'orders' : resultsOnly ? 'results' : skipRecords ? 'no-records' : 'full'].join(':');
  const cached = raidControlTimelineCache.get(requestKey);
  if (cached && Date.now() - cached.cachedAt < RAID_CONTROL_TIMELINE_CACHE_MS) return Promise.resolve(cached.value);

  return dedupeRaidControlRequest(raidControlTimelineRequests, requestKey, async () => {
    const runtime = await resolveSeasonRuntime({ unionId, seasonId });
    const partKey = [String(unionId || ''), String(seasonId || ''), Number(limit || 160)].join(':');
    const [snapshot, orderResult, raidResult, bossNames] = await Promise.all([
      // 플래그는 질의 자체를 건너뛰고, 캐시는 건너뛰지 않은 쪽이 방금 받아 온 것과 같을 때 덮는다.
      ordersOnly || resultsOnly ? Promise.resolve(null) : loadRaidControlRealtimeSnapshot({ seasonNo: runtime.seasonNo }),
      resultsOnly ? Promise.resolve({ data: [], error: null }) : cachedPart('orders', raidControlOrdersCache, raidControlOrdersRequests, partKey, () => client().from('raid_control_orders').select('id,union_id,season_id,target_member_openid,target_user_id,target_nickname,boss_no,phase_no,practice_record_id,practice_title,expected_damage_eok,deck,note,status,created_by,acknowledged_at,started_at,completed_at,created_at,updated_at').eq('union_id', unionId).eq('season_id', seasonId).order('created_at', { ascending: false }).limit(limit)),
      ordersOnly || skipRecords ? Promise.resolve({ data: [], error: null }) : cachedPart('records', raidControlRecordsCache, raidControlRecordsRequests, partKey, () => client().from('raid_records').select('id,union_id,season_id,record_key,member_openid,member_nickname,boss_no,phase_no,attack_no,damage_eok,is_kill,attacked_at,deck,created_at,updated_at').eq('union_id', unionId).eq('season_id', seasonId).order('created_at', { ascending: false }).limit(limit)),
      loadBossNames(unionId, seasonId),
    ]);
    if (orderResult.error) throw controlError(orderResult.error, '공격 지시 내역을 불러오지 못했습니다.');
    if (raidResult.error) throw controlError(raidResult.error, '실전 결과 내역을 불러오지 못했습니다.');
    const orders = (orderResult.data || []).map(normalizeOrder);
    const realtimeMessages = (Array.isArray(snapshot?.messages) ? snapshot.messages : [])
      .map((row) => normalizeMessage(row, { unionId, seasonId }))
      .filter((message) => message.id && message.type !== 'attack_order' && message.type !== 'raid_result');
    const raidResultMessages = (raidResult.data || [])
      .map((row) => syntheticRaidResultMessage(row, runtime, bossNames))
      .filter(Boolean);
    const orderMessages = orders.map((order) => syntheticOrderMessage(order, runtime, bossNames)).filter(Boolean);
    // Date.parse rather than new Date(...).getTime(): the timeline is rebuilt on every relayed change,
    // and a Date object per comparison is the expensive part of that sort.
    const messageTime = (message) => {
      const parsed = Date.parse(message?.createdAt || '');
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const messages = [...realtimeMessages, ...raidResultMessages, ...orderMessages]
      .sort((left, right) => messageTime(left) - messageTime(right));
    const value = {
      messages,
      orders,
      lastReadAt: String(snapshot?.lastReadAt || ''),
    };
    if (generation === timelineGeneration) raidControlTimelineCache.set(requestKey, { cachedAt: Date.now(), value });
    return value;
  });
}

export async function sendRaidControlMessage({ seasonId, body, stickerId = null, stickerIds = null, mentionedUserIds = [], nikkeRefs = [], replyToId = null } = {}) {
  try {
    const runtime = await resolveSeasonRuntime({ seasonId });
    const message = await sendRaidControlRealtimeMessage({
      seasonNo: runtime.seasonNo,
      messageKind: stickerId || stickerIds?.length ? 'sticker' : 'text',
      stickerId,
      stickerIds,
      body,
      mentionedUserIds: [...new Set((mentionedUserIds || []).filter(Boolean))].slice(0, 32),
      nikkeRefs: (Array.isArray(nikkeRefs) ? nikkeRefs : []).slice(0, 5),
      replyToId,
    });
    rememberRealtimeMessage(message?.id, runtime.seasonNo);
    // 채팅은 지시·실전 목록을 바꾸지 않는다. 합쳐 둔 타임라인만 다시 짜면 된다.
    clearRaidControlTimelineCache({ orders: false, records: false });
    return String(message?.id || '');
  } catch (error) {
    throw controlError(error, '메시지를 보내지 못했습니다.');
  }
}

export async function sendRaidControlPracticeReport({ seasonId, targetMemberOpenid, bossNo, phaseNo, damageEok, body = '', nikkeRefs = [] } = {}) {
  try {
    const runtime = await resolveSeasonRuntime({ seasonId });
    const sessionResult = await client().auth.getSession();
    const userId = String(sessionResult.data?.session?.user?.id || '');
    const selfMember = (runtime.members || []).find((member) => member.userId === userId) || null;
    if (targetMemberOpenid && selfMember?.memberOpenid && String(targetMemberOpenid) !== selfMember.memberOpenid) {
      throw new Error('target_member_not_found');
    }
    const message = await sendRaidControlRealtimeMessage({
      seasonNo: runtime.seasonNo,
      messageKind: 'practice_report',
      body,
      nikkeRefs: (Array.isArray(nikkeRefs) ? nikkeRefs : []).slice(0, 5),
      practice: { bossNo: Number(bossNo), phaseNo: Number(phaseNo), damageEok: Number(damageEok) },
    });
    rememberRealtimeMessage(message?.id, runtime.seasonNo);
    // 채팅은 지시·실전 목록을 바꾸지 않는다. 합쳐 둔 타임라인만 다시 짜면 된다.
    clearRaidControlTimelineCache({ orders: false, records: false });
    return String(message?.id || '');
  } catch (error) {
    throw controlError(error, '모의전 결과를 공유하지 못했습니다.');
  }
}

export async function createRaidControlOrder(payload) {
  const { data, error } = await client().rpc('create_raid_control_order_for_season', raidControlOrderArgs(payload));
  if (error) throw controlError(error, '공격 지시를 보내지 못했습니다.');
  const result = { orderId: String(data?.order_id || ''), messageId: String(data?.message_id || ''), targetUserId: String(data?.target_user_id || '') };
  if (result.orderId && result.messageId) orderMessageIds.set(result.orderId, result.messageId);
  // 지시만 바뀐다. 실전 기록은 그대로이므로 다시 받아 올 이유가 없다.
  clearRaidControlTimelineCache({ records: false });
  return result;
}

export async function createRaidControlCustomOrder(payload) {
  const { data, error } = await client().rpc('create_raid_control_custom_order_for_season', raidControlCustomOrderArgs(payload));
  if (error) throw controlError(error, '직접 작성한 공격 지시를 보내지 못했습니다.');
  const result = { orderId: String(data?.order_id || ''), messageId: String(data?.message_id || ''), targetUserId: String(data?.target_user_id || '') };
  if (result.orderId && result.messageId) orderMessageIds.set(result.orderId, result.messageId);
  // 지시만 바뀐다. 실전 기록은 그대로이므로 다시 받아 올 이유가 없다.
  clearRaidControlTimelineCache({ records: false });
  return result;
}

export async function updateRaidControlOrderStatus(orderId, status, { confirmConcurrent = false } = {}) {
  const { data, error } = await client().rpc('update_raid_control_order_status', { p_order_id: orderId, p_status: status, ...(confirmConcurrent ? { p_confirm_concurrent: true } : {}) });
  if (error) throw controlError(error, '공격 상태를 변경하지 못했습니다.');
  // 지시만 바뀐다. 실전 기록은 그대로이므로 다시 받아 올 이유가 없다.
  clearRaidControlTimelineCache({ records: false });
  return normalizeOrder(data || {});
}

export async function markRaidControlRead({ unionId, userId, at = new Date().toISOString() }) {
  if (!unionId || !userId) return;
  try {
    const seasonId = activeSeasonIdByUnion.get(String(unionId)) || '';
    const runtime = await resolveSeasonRuntime({ unionId, seasonId });
    await markRaidControlRealtimeRead({ seasonNo: runtime.seasonNo, at });
    // 채팅은 지시·실전 목록을 바꾸지 않는다. 합쳐 둔 타임라인만 다시 짜면 된다.
    clearRaidControlTimelineCache({ orders: false, records: false });
  } catch (error) {
    throw controlError(error, '읽음 상태를 저장하지 못했습니다.');
  }
}

function raidControlSubscriptionKey(unionId, seasonId) {
  return `${String(unionId || '').trim()}:${String(seasonId || '').trim()}`;
}

function createRaidControlSubscriptionStore(unionId, seasonId) {
  const key = raidControlSubscriptionKey(unionId, seasonId);
  const store = {
    key,
    unionId: String(unionId || ''),
    seasonId: String(seasonId || ''),
    listeners: new Set(),
    realtimeSubscription: null,
    realtimeStatus: 'connecting',
    releaseTimer: null,
    closed: false,
    snapshotSeen: false,
    knownRealtimeMessageIds: new Set(),
    presence: [],
  };

  // A read receipt says where someone has read up to; it changes no message, order or timeline row.
  // Invalidating for it threw away the 5s cache and, worse, the in-flight dedupe map, so a receipt
  // arriving mid-fetch turned one timeline request into two. The room already ignores these changes.
  // 무엇이 바뀌었는지에 따라 비울 것만 비운다. 전에는 무엇이 오든 두 목록을 다 버려서, 공격
  // 상태 하나를 바꿔도 화면을 열어 둔 사람마다 지시 160줄 + 실전 160줄을 다시 받아 왔다.
  const invalidationFor = (change) => {
    if (change?.source === 'domain') {
      if (change.domain === 'orders') return { records: false };
      if (change.domain === 'results') return { orders: false };
      return undefined; // practice·roster·config 는 방 정보를 다시 읽는 길로 간다.
    }
    if (change?.source === 'order') return { records: false };
    // 채팅·스냅샷·재동기화. 메시지는 목록을 바꾸지 않지만 합쳐 둔 타임라인은 다시 짜야 한다.
    if (['message', 'snapshot'].includes(change?.source)) return { orders: false, records: false };
    return undefined;
  };

  const relay = (change) => {
    if (!['read', 'chat-header', 'presence'].includes(change?.source)) clearRaidControlTimelineCache(invalidationFor(change));
    if (change?.source === 'chat-header') store.chatHeader = change.header;
    store.listeners.forEach((listener) => listener.timelineChange(change));
  };

  void resolveSeasonRuntime({ unionId, seasonId }).then((runtime) => {
    if (store.closed) return;
    store.realtimeSubscription = subscribeRaidControlRealtime({
      seasonNo: runtime.seasonNo,
      // 스냅샷은 접속할 때와 재연결할 때마다 온다(폰에서는 앱을 전환할 때마다). 이걸 메시지마다
      // 따로 흘리면 받는 쪽이 160번 목록을 통째로 복사하고 160번 다시 정렬한다. 한 덩어리로 넘긴다.
      onSnapshot: (snapshot) => {
        relay({ source: 'chat-header', header: normalizeChatHeader(snapshot?.chatHeader) });
        store.presence = Array.isArray(snapshot?.presence) ? snapshot.presence : [];
        relay({ source: 'presence', users: store.presence });
        const messages = [];
        const nextIds = new Set();
        for (const rawRow of Array.isArray(snapshot?.messages) ? snapshot.messages : []) {
          const message = normalizeMessage(rawRow, { unionId: store.unionId, seasonId: store.seasonId });
          if (!message.id || message.type === 'attack_order' || message.type === 'raid_result') continue;
          nextIds.add(message.id);
          messages.push(message);
        }
        const removedIds = [];
        for (const previousId of store.knownRealtimeMessageIds) if (!nextIds.has(previousId)) removedIds.push(previousId);
        store.knownRealtimeMessageIds = nextIds;
        relay({ source: 'snapshot', messages, removedIds, row: { unionId: store.unionId, seasonId: store.seasonId } });
        // resync 는 '자리를 비운 사이 지시·실전이 바뀌었을 수 있다'는 뜻이다. 방에 처음 들어올 때의
        // 첫 스냅샷에는 해당되지 않는다 - 바로 직전에 같은 것을 받아 왔는데 한 번 더 받아 오게 된다.
        if (store.snapshotSeen) relay({ source: 'resync', row: { unionId: store.unionId, seasonId: store.seasonId } });
        store.snapshotSeen = true;
      },
      // 재연결·인증 대기와 정상 연결이 구분되지 않아, 입력은 되는데 전송만 안 되는 것처럼
      // 보였다. 상태를 같은 통로로 흘려 화면이 알 수 있게 한다.
      onStatus: ({ status }) => {
        store.realtimeStatus = String(status || '');
        relay({ source: 'realtime-status', status: store.realtimeStatus });
      },
      onEvent: (event) => {
        // 관제 방은 자기 소켓으로 '지금 이 방에 누가 있는지'를 이미 알고 있고, 붙고 떨어질
        // 때마다 그 명단을 쏘고 있었다. 받아만 두고 쓰지 않던 것을 화면까지 올린다.
        if (event?.type === 'presence' && Array.isArray(event.users)) {
          store.presence = event.users;
          relay({ source: 'presence', users: event.users });
          return;
        }
        if (event?.type === 'chat.header' || event?.type === 'chat.header.ack') {
          relay({ source: 'chat-header', header: normalizeChatHeader(event.header) });
          return;
        }
        if (event?.type === 'domain.change') {
          if (event.unionId === store.unionId && event.seasonId === store.seasonId && event.domain === 'config') bossNamesBySeason.delete(`${store.unionId}:${store.seasonId}`);
          if (event.unionId === store.unionId && event.seasonId === store.seasonId) relay({ source: 'domain', domain: event.domain, row: event });
        } else if (event?.type === 'message.upsert' && event.message) {
          const message = normalizeMessage(event.message, { unionId: store.unionId, seasonId: store.seasonId });
          if (!message.id || message.type === 'attack_order' || message.type === 'raid_result') return;
          store.knownRealtimeMessageIds.add(message.id);
          relay({ source: 'message', eventType: 'UPSERT', rowId: message.id, row: message, deleted: false });
        } else if (event?.type === 'message.delete' && event.messageId) {
          if (store.chatHeader?.pinnedMessage?.id === event.messageId) relay({ source: 'chat-header', header: { ...store.chatHeader, pinnedMessage: null } });
          const rowId = String(event.messageId);
          store.knownRealtimeMessageIds.delete(rowId);
          relay({ source: 'message', eventType: 'DELETE', rowId, row: { id: rowId, unionId: store.unionId, seasonId: store.seasonId }, deleted: true });
        } else if (event?.type === 'season.chat_purged') {
          if (store.chatHeader) relay({ source: 'chat-header', header: { ...store.chatHeader, pinnedMessage: null } });
          relay({ source: 'messages-cleared' });
          for (const previousId of store.knownRealtimeMessageIds) {
            relay({ source: 'message', eventType: 'DELETE', rowId: previousId, row: { id: previousId, unionId: store.unionId, seasonId: store.seasonId }, deleted: true });
          }
          store.knownRealtimeMessageIds = new Set();
        } else if (event?.type === 'read.ack') {
          relay({ source: 'read', lastReadAt: event.lastReadAt });
        }
      },
    });

  }).catch(() => {});

  return store;
}

function scheduleRaidControlSubscriptionRelease(store) {
  if (store.listeners.size || store.releaseTimer) return;
  store.releaseTimer = globalThis.setTimeout(() => {
    store.releaseTimer = null;
    if (store.listeners.size || raidControlSubscriptionStores.get(store.key) !== store) return;
    raidControlSubscriptionStores.delete(store.key);
    store.closed = true;
    store.realtimeSubscription?.unsubscribe();
    store.realtimeSubscription = null;
  }, REALTIME_CHANNEL_RELEASE_DELAY_MS);
}

// 앱에서 돌아왔을 때 쓰는 통로. 상시 구독이 들고 있는 소켓이 정말 통하는지 확인하고, 아니면
// 다시 건다. 소켓이 죽은 줄 모르면 타임라인 조회도 그 죽은 연결의 옛 스냅샷을 받아 간다.
export function resyncRaidControlConnection({ unionId, seasonId } = {}) {
  return resolveSeasonRuntime({ unionId, seasonId })
    .then((runtime) => resyncRaidControlRealtime(runtime.seasonNo))
    .catch(() => false);
}

export function subscribeRaidControl({ unionId, seasonId, onTimelineChange }) {
  const key = raidControlSubscriptionKey(unionId, seasonId);
  let store = raidControlSubscriptionStores.get(key);
  if (!store) {
    store = createRaidControlSubscriptionStore(unionId, seasonId);
    raidControlSubscriptionStores.set(key, store);
  }
  if (store.releaseTimer) {
    globalThis.clearTimeout(store.releaseTimer);
    store.releaseTimer = null;
  }

  const listener = {
    timelineChange: typeof onTimelineChange === 'function' ? onTimelineChange : () => {},
  };
  store.listeners.add(listener);
  // Shared sockets can already be open when a screen mounts or re-subscribes.
  // Replay the current status so it does not wait for another socket transition.
  queueMicrotask(() => {
    if (!store.closed && store.listeners.has(listener)) {
      listener.timelineChange({ source: 'realtime-status', status: store.realtimeStatus });
      if (store.chatHeader) listener.timelineChange({ source: 'chat-header', header: store.chatHeader });
      // 접속 명단도 같이 들려준다. 관제 방 효과는 방 정보가 바뀔 때마다 다시 붙는데, 그때마다
      // 명단을 못 받으면 누가 들고 날 때까지 빈 채로 남는다 - 들어가자마자 아무도 없어 보인다.
      listener.timelineChange({ source: 'presence', users: store.presence || [] });
    }
  });

  return () => {
    store.listeners.delete(listener);
    scheduleRaidControlSubscriptionRelease(store);
  };
}

export async function requestRaidControlPush(messageId, { seasonNo = 0 } = {}) {
  const id = String(messageId || '').trim();
  if (!id) return null;
  const explicitSeasonNo = Number(seasonNo || 0);
  const realtimeSeasonNo = realtimeMessageSeasonNos.get(id)
    || (UUID_RE.test(id) && Number.isInteger(explicitSeasonNo) && explicitSeasonNo > 0 ? explicitSeasonNo : 0);
  const body = realtimeSeasonNo
    ? { realtimeMessageId: id, seasonNo: realtimeSeasonNo }
    : { messageId: id };
  const { data, error } = await client().functions.invoke('send-raid-control-push', { body });
  if (error) throw error;
  return data;
}

export async function loadRaidControlHistory({ unionId, seasonId, beforeAt, beforeId }) {
  const runtime = await resolveSeasonRuntime({ unionId, seasonId });
  const page = await loadRaidControlRealtimeHistory({ seasonNo: runtime.seasonNo, beforeAt, beforeId });
  return { hasMore: Boolean(page.hasMore), messages: (page.messages || [])
    .map(row => normalizeMessage(row, { unionId, seasonId })) };
}

function normalizeChatHeader(header = {}) {
  return { notice: String(header?.notice || ''), updatedAt: String(header?.updatedAt || ''),
    pinnedMessage: header?.pinnedMessage?.id ? normalizeMessage(header.pinnedMessage) : null };
}
export async function updateRaidControlChatHeader({ unionId, seasonId, action, body, messageId }) {
  const runtime = await resolveSeasonRuntime({ unionId, seasonId });
  try {
    const result = await updateRaidControlRealtimeHeader({ seasonNo: runtime.seasonNo, action, body, messageId });
    return normalizeChatHeader(result.header);
  } catch (error) { throw controlError(error, '공지·고정을 변경하지 못했습니다.'); }
}
